// Racket Lab Admin — English base translations (i18n QUICK 1.A scaffolding).
//
// SCAFFOLDING ONLY. No JSX consumes this file yet — migration happens
// incrementally in QUICK 1.B-G under separate GOs.
//
// Tree structure mirrors `racketLab.es.js` 1:1. Adding a new key here REQUIRES
// the same key in `racketLab.es.js`. Audit-only files imported from
// `racketLabAdminI18n.js` wrapper (not yet created — comes in Q1.B).
//
// Coverage scope per RACKET_LAB_ADMIN_I18N_QUICK_1_ANALYSIS_v1.md:
//   - 19 namespaces · ~455 keys
//   - 10 enum → label mappings declared in racketLabMaps.js
//
// HARD RULES (do not break):
//   1. Values may be plain strings only — no inline JSX.
//   2. Snake_case enum values (e.g. `shop_manager`) NEVER appear here as keys.
//      Use semantic kebab/camelCase identifiers in the tree.
//   3. Order of keys mirrors es.js exactly (eases diff during translation review).
//   4. Keep button/badge strings short — they render in tight chrome.

const racketLabEn = {
  // SS-8/CP-1.3 — RBAC section guard panel.
  guard: {
    denied_title: "Section not available",
    denied_body: "Your profile does not have access to this section of the Racket Lab console.",
    back_to_dashboard: "Back to dashboard",
  },

  nav: {
    groups: {
      core: "Core",
      workforce: "Workforce",
      qr_notifications: "QR & Notifications",
      ops_toolkit: "Ops Toolkit",
    },
    items: {
      dashboard: "Dashboard",
      shops: "Shops",
      tickets: "Tickets",
      my_tickets: "My tickets",
      calendar: "Calendar",
      operators: "Operators",
      shifts: "Shifts",
      capabilities: "Capabilities",
      inventory: "Inventory",
      qr_tokens: "QR Tokens",
      resolve: "Resolve",
      envelopes: "Envelopes",
      mock_outbox: "Mock Outbox",
      toolkit: "Toolkit",
      promote: "Promote",
      gating: "Gating",
      redis: "Redis",
      scan_audit: "Scan audit",
      legacy: "Legacy",
      retention: "Retention",
      seed_helper: "Seed helper",
    },
  },

  common: {
    actions: {
      cancel: "Cancel",
      confirm: "Confirm",
      apply: "Apply",
      save: "Save",
      close: "Close",
      open: "Open",
      toggle: "Toggle",
      reset: "Reset",
      reveal: "Reveal",
      hide: "Hide",
      copy: "Copy",
      refresh: "Refresh",
      submit: "Submit",
      lookup: "Lookup",
      create: "Create",
      verify: "Verify",
      resolve: "Resolve",
      mint: "Mint",
      load_into_promote: "Load into Promote form",
    },
    destructive: {
      revoke: "Revoke",
      permanently_destroy: "Permanently destroy",
      force_on: "Force ON",
      force_off: "Force OFF",
      clear_override: "Clear override",
      not_reversible: "NOT reversible",
      type_to_confirm: "Type {phrase} to confirm",
    },
    labels: {
      status: "Status",
      country: "Country",
      organisation: "Organisation",
      shop: "Shop",
      shop_id: "Shop ID",
      shop_ids: "Shop IDs",
      user: "User",
      user_id: "User ID",
      role: "Role",
      name: "Name",
      display_name: "Display name",
      code: "Code",
      customer: "Customer",
      created: "Created",
      created_at: "Created at",
      actions: "Actions",
      result: "Result",
      reason: "Reason",
      bucket: "Bucket",
      language: "Language",
      channel: "Channel",
      recipient: "Recipient",
      dry_run: "Dry run",
      capability: "Capability",
      ttl: "TTL",
      single_use: "Single use",
      kind: "Kind",
      state: "State",
      expires_at: "Expires at",
      used_at: "Used at",
      entity: "Entity",
      scope: "Scope",
      revoke_reason: "Revoke reason",
      template_key: "Template key",
      from_role: "From role",
      dispatch_ref: "Dispatch ref",
      dispatched_at: "Dispatched at",
      label: "Label",
      notes: "Notes",
    },
    empty_state: "No items yet.",
    unknown: "Unknown",
    columns: {
      id: "ID",
    },
    filters: {
      any_option: "(any)",
    },
    list: {
      counter_items: "{count} items",
      counter_of_total: "{count} of {total}",
    },
  },

  dashboard: {
    title: "Dashboard",
    page_title: "Racket Lab · Admin Dashboard",
    page_subtitle: "Pilot: {org} · Read-only · Track A",
    loading: "Loading…",
    error_prefix: "Error",
    error_unknown: "unknown",
    promotion_overview: "Promotion overview",
    totals_total: "Total",
    totals_active: "Active",
    totals_roles: "Roles",
    dispatch_card_title: "Notifications · dispatch-status",
    dispatch_outbox_line: "Outbox: {count} · TTL: {days}d",
    redis_card_title: "Redis health (R2)",
    redis_flag_label: "redis",
    redis_healthy: "healthy",
    redis_down: "down/disabled",
    redis_backend_line: "Backend: {backend}",
    legacy_card_title: "Legacy memberships (S9.1)",
    legacy_unknown_label: "Unknown",
    legacy_classified_label: "Classified",
    landing: {
      quick_actions_title: "Quick actions",
      quick_new_ticket: "New ticket",
      quick_tickets: "Tickets",
      quick_my_tickets: "My tickets",
      quick_calendar: "Calendar",
      quick_calendar_soon: "SS-4 · coming soon",
      stat_tickets_total: "Total tickets",
      stat_active_queue: "Active queue",
      stat_in_progress: "In progress",
      stat_ready_for_pickup: "Ready for pickup",
      stat_operators_active: "Active operators",
      stat_shops_active: "Active shops",
      stat_open_shifts: "Open shifts",
      stat_pending_envelopes: "Pending envelopes",
      stat_low_stock: "Low stock items",
      scoped_banner: "Scoped view · profile: {profile}",
      qa_note: "QA/test tickets: {n} (excluded from operational counters)",
      queue_title: "Work queue",
      queue_empty: "No active tickets.",
      recent_title: "Recent tickets",
      recent_empty: "No tickets yet.",
      recent_view_all: "View all",
      tensions_title: "Most used tensions by shop",
      tensions_empty: "No tensions recorded yet (SS-2.1 structured data).",
      tensions_hint: "Admin-only summary · built on SS-2.1 structured stringing fields.",
      agenda_title: "Agenda / calendar",
      agenda_open_shifts: "{count} shift(s) open now",
      agenda_today_title: "Today's services",
      agenda_today_empty: "No services scheduled for today.",
      agenda_open_calendar: "Open calendar",
      agenda_calendar_placeholder: "The admin calendar (month/day view) lands in SS-4. This block reserves its spot.",
      ops_section_title: "Platform / Ops",
      catalog_title: "Central catalog consumption",
      catalog_products_available: "Catalog products available",
      catalog_brands_available: "Catalog brands available",
      catalog_tickets_linked: "Tickets linked to catalog",
      catalog_inventory_linked: "Inventory linked to catalog",
      catalog_hint: "Read-only · source: Catalog Intelligence (CATALOG-4).",
      cp_cohort_title: "Controlled-Production cohort",
      cp_cohort_count: "{n} provisioned member(s)",
      cp_cohort_open: "Open cohort console",
      cp_cohort_hint: "Admin-only · CP-1.2 provisioning surface.",
    },
    cards: {
      shops_total: "Shops",
      tickets_total: "Tickets",
      operators_total: "Operators",
      open_shifts: "Open shifts",
      active_tokens: "Active QR tokens",
      pending_envelopes: "Pending envelopes",
    },
  },

  shops: {
    title: "Shops",
    page_header: "Workforce · Shops",
    counter: "{count} shop · read-only",
    counter_plural: "{count} shops · read-only",
    empty: "No shops registered yet.",
    columns: {
      name: "Name",
      country: "Country",
      organisation: "Organisation",
      shop_id: "Shop ID",
      service_mode: "Service mode",
    },
    service_mode: {
      tooltip: "Service mode",
      in_shop: "in-shop",
      mobile: "mobile",
      hybrid: "hybrid",
    },
    detail: {
      title: "Shop detail",
      subtitle: "Read-only · Future panels (machines · capabilities · operators · shifts) land in Sub-sprint B",
      back: "Back to shops",
      loading: "Loading shop {id}…",
      not_found: "Shop {id} not found.",
      created_at: "Created",
      status: "Status",
      tension_preset: {
        title: "Default stringing tension preset",
        admin_only: "Admin · staff only",
        hint: "Optional. When set, new tickets created for this shop autofill these values if the operator leaves the tension fields empty. Manual edits always win.",
        empty: "No preset configured for this shop yet.",
        unit: "Unit",
        main: "Main (vertical)",
        cross: "Cross (horizontal)",
        save: "Save preset",
        saving: "Saving…",
        clear: "Clear preset",
      },
      low_stock_threshold: {
        title: "Default low-stock threshold (fallback)",
        admin_only: "Admin · staff only",
        hint: "Optional. Acts as a SHOP-LEVEL fallback for items that don't have their own per-item low-stock threshold. Items with a per-item threshold are unaffected.",
        empty: "No shop-level fallback configured.",
        value: "Threshold (qty)",
        save: "Save threshold",
        saving: "Saving…",
        clear: "Clear threshold",
      },
      service_mode_section_title: "Service mode · settings request",
      request_change_btn: "Request settings change",
      request_change_hint: "shop_manager can submit a change request here; admin/staff review and apply manually via PATCH /shops/{id}. ZERO real notifications fire.",
      request_change_placeholder: "Describe the desired change…",
      cancel: "Cancel",
      submit_request: "Submit request",
      submitting: "Submitting…",
      requests_history: "Requests on this shop",
      requests_empty: "No requests yet.",
      loading_list: "Loading…",
      decision_reason: "Decision",
      approve: "Approve",
      reject: "Reject",
      cancel_request: "Cancel request",
      req_status_pending: "pending",
      req_status_approved: "approved",
      req_status_rejected: "rejected",
      req_status_cancelled: "cancelled",
    },
  },

  tickets: {
    title: "Tickets",
    qa_badge: "QA test",
    counter: "{count} ticket · read-only",
    counter_plural: "{count} tickets · read-only",
    empty: "No tickets registered yet.",
    new_button: "New ticket",
    columns: {
      code: "Code",
      status: "Status",
      shop: "Shop",
      customer: "Customer",
      created: "Created",
    },
    detail: {
      title: "Ticket detail",
      subtitle: "Lifecycle via per-action endpoints · Audit timeline below",
      back: "Back to tickets",
      loading: "Loading ticket…",
      not_found: "Ticket {id} not found.",
      ticket_id: "Ticket ID",
      customer_demo: "Customer (demo)",
      racket: "Racket",
      player_synthetic: "Player ID (synthetic)",
      notes: "Notes",
      stringing_title: "Stringing · Racket Lab",
      product_type: "Material type",
      brand: "Brand",
      model: "Model",
      accessory: "Accessory / add-on",
      string_main: "Main string (vertical)",
      string_cross: "Cross string (horizontal)",
      tension: "Tension (main / cross)",
      string_origin: "String origin",
      supply_format: "Supplied format",
      customer_string_notes: "Customer material notes",
      profile_type: "Profile / context",
      inventory_item: "Inventory (set/reel)",
      inventory_consumption: "Inventory consumption",
      inventory_consumed: "consumed",
      inventory_not_consumed: "no consumption recorded",
      scheduled_for: "Scheduled for",
    },
    stepper: {
      current_status: "Current status",
    },
    create: {
      title: "New ticket · admin-only",
      close: "Close",
      cancel: "Cancel",
      submit: "Create ticket",
      submitting: "Creating…",
      error_unknown: "Unknown error while creating the ticket.",
      gear3: {
        title: "My Gear suggestion",
        preview_badge: "preview · mock",
        toggle: "Show seed mock data",
        banner: "Preview only · no customer data · no DB writes · no ConsentGate · no real notifications · no marketplace offer.",
        apply: "Apply My Gear suggestion",
        disabled_hint: "Toggle ON to load 3 mock rackets (Racket 1 default / Racket 2 / Racket 3 · pending canonical). Suggestions only fill empty fields — manual override, shop preset and operator preset all win.",
        report: {
          title: "Suggestion applied (local only · not submitted)",
          filled: "Filled",
          preserved: "Preserved (manual / shop / operator preset already set)",
          hierarchy_note: "Hierarchy: manual override > customer/racket profile > shop preset > operator preset > blank.",
        },
      },
      shop_label: "Shop *",
      shop_placeholder: "Select a shop…",
      capability_label: "Capability *",
      capability_placeholder: "Select a service…",
      equipment_label: "Equipment label *",
      equipment_placeholder: "e.g. racket-X1 / stringing-16x19",
      customer_label: "Customer label",
      customer_placeholder: "e.g. customer-demo-Q26 (no PII)",
      notes_label: "Equipment notes",
      notes_placeholder: "Racket condition, string brand, preferred tension…",
      operator_label: "Operator (optional)",
      operator_placeholder: "racket_lab_operator_id",
      operator_hint: "If filled, ticket starts in status 'assigned'.",
      operator_pick_shop_first: "Select a shop first",
      operator_empty_shop: "No active operators in this shop",
      operator_unassigned: "— Unassigned —",
      operator_placeholder_tag: "placeholder",
      operator_use_manual: "Use UUID instead",
      operator_use_select: "Use scoped selector",
      operator_search_placeholder: "Search operators by name…",
      operator_loading: "Searching…",
      operator_no_results: "No operators match this search",
      operator_clear: "Clear operator",
      machine_label: "Machine (optional)",
      machine_placeholder: "racket_lab_machine_id",
      anti_pii_hint: "No emails or phones · backend rejects '@' and 7+ digits.",
      initial_status_hint_draft: "Initial status: draft",
      initial_status_hint_assigned: "Initial status: assigned (with operator)",
      section_product_title: "Product / material",
      section_stringing_title: "Stringing",
      section_context_title: "Context",
      catalog_mock_hint: "Tennis rackets & strings now come from the central catalog (Catalog Intelligence, read-only). Padel/other still use the temporary mock · manual entry always available.",
      string_catalog_label: "From central catalog (optional)",
      string_catalog_placeholder: "— Select catalog string —",
      product_type_label: "Material type",
      product_type_placeholder: "Select type…",
      brand_label: "Brand",
      brand_placeholder: "Select brand…",
      brand_custom_label: "Brand (free text)",
      brand_custom_placeholder: "Type the brand…",
      model_label: "Model",
      model_placeholder: "Select model…",
      model_custom_label: "Model (free text)",
      model_custom_placeholder: "Type the model…",
      option_other: "Other…",
      accessory_label: "Accessory / add-on",
      accessory_placeholder: "e.g. grip, bumper, dampener…",
      string_main_label: "Main string (vertical)",
      string_main_placeholder: "e.g. Babolat RPM Blast",
      string_cross_label: "Cross string (horizontal)",
      string_cross_placeholder: "Same or different from main",
      string_copy_main: "= main",
      tension_main_label: "Main tension",
      tension_cross_label: "Cross tension",
      tension_unit_label: "Unit",
      tension_placeholder: "1–80",
      tension_preset_banner: "Default tension preset applied from the selected shop. You can override any value manually.",
      tension_preset_banner_operator: "Default tension preset applied from the selected operator/technician for the remaining empty fields. You can override any value manually.",
      string_origin_label: "String origin",
      string_origin_placeholder: "Select origin…",
      supply_format_label: "Customer string format",
      supply_format_placeholder: "Set or reel…",
      customer_string_notes_label: "Customer material notes",
      customer_string_notes_placeholder: "Condition of the customer-supplied set/reel…",
      profile_type_label: "User profile / context",
      profile_type_placeholder: "Select profile…",
      profile_type_hint: "Admin-only design · no customer-facing effects.",
      scheduled_label: "Scheduled date (optional)",
      scheduled_hint: "Shows up in the admin calendar (SS-4).",
      inventory_item_label: "Shop inventory (set/reel)",
      inventory_item_placeholder: "Link an inventory item… (optional)",
      inventory_item_hint: "Reference only · stock is NOT deducted automatically (SS-6.1).",
    },
    stringing: {
      product_type_racket: "Racket (tennis)",
      product_type_padel_racket: "Padel racket",
      product_type_accessory: "Accessory",
      product_type_other: "Other",
      origin_shop: "Shop string",
      origin_customer: "Customer-supplied string",
      supply_set: "Set",
      supply_reel: "Reel",
      profile_player: "Player",
      profile_parent_guardian: "Parent / Guardian",
      profile_coach: "Coach",
      profile_stringer: "Technician / stringer",
      profile_shop_operator: "Operator / shop",
      profile_admin_staff: "Admin / staff",
    },
    qr_canonical: {
      title: "Canonical ticket QR",
      subtitle: "Admin-only · no public scan · traceability via qr_tokens",
      loading: "Loading QR…",
      none: "This ticket has no canonical QR yet.",
      generate: "Generate canonical QR",
      generating: "Generating…",
      payload_label: "Payload",
      expires_label: "Expires",
      scan_count_label: "Scans",
      print_view: "Print view",
      print: {
        title: "Racket Lab Ticket · QR",
        print_btn: "Print",
        back: "Back to ticket",
        status_label: "Status",
        customer_label: "Customer",
        equipment_label: "Equipment",
        strings_label: "Strings (M/C)",
        tension_label: "Tension",
        scheduled_label: "Scheduled",
        shop_label: "Shop",
        operator_label: "Operator",
        not_found: "QR not found. Generate the canonical QR from the ticket detail.",
      },
    },
    photos: {
      title: "Equipment photos",
      subtitle: "Admin-only visual evidence · private storage · signed URLs (5 min) · soft delete",
      loading: "Loading photos…",
      empty: "This ticket has no photos yet.",
      upload_btn: "Upload photo",
      uploading: "Uploading…",
      upload_hint: "JPEG/PNG/WebP/HEIC · max 8MB · max 20 photos per ticket",
      caption_placeholder: "Short caption (optional)…",
      phase_check_in: "Check-in",
      phase_in_service: "In service",
      phase_final: "Final",
      phase_pickup: "Pickup",
      delete_btn: "Remove (soft delete)",
      delete_confirm: "Remove this photo? (soft delete, admin-recoverable)",
      print_label: "Photo evidence",
      print_count: "{count} photo(s) on file",
    },
    qa: {
      title: "What you'll see as a QA tester",
      step_1: "Click 'New ticket' in the top-right corner.",
      step_2: "Pick Shop + Capability (both required).",
      step_3: "Type a descriptive 'Equipment label' (no email, no phone).",
      step_4: "Optional: fill customer / operator / machine / notes.",
      step_5: "Click 'Create ticket'. The console redirects you to the detail.",
      step_6: "Check the initial status: 'draft' without operator · 'assigned' if you added one.",
      step_7: "Verify the Stepper shows the correct allowed actions for that status.",
      pass_label: "PASS",
      pass_criteria: "Ticket created · initial status coherent · Stepper shows expected actions · audit timeline visible.",
      fail_label: "FAIL",
      fail_criteria: "422 without clear reason · wrong initial status · Stepper empty in non-terminal state · timeline missing.",
      cta_open_new: "Open 'New ticket' now",
    },
  },
  my_tickets: {
    title: "My tickets",
    subtitle: "Operator work queue · admin-only · preview-first",
    operator_label: "Operator",
    operator_placeholder: "Select an operator…",
    no_operator_selected: "Select an operator to see their queue.",
    counter: "{count} active ticket(s)",
    include_terminal_label: "Include terminals (closed/cancelled/deleted)",
    loading: "Loading tickets…",
    empty_no_operator: "No operator selected yet. Pick one in the dropdown above to see their ticket queue.",
    empty_for_operator: "This operator has no tickets in the visible states. Toggle 'Include terminals' to see history.",
    low_stock_badge: "Low stock",
    insufficient_stock_badge: "Insufficient stock",
    stock_hint: "{available} {unit} available · {needed} needed per ticket",
  },

  calendar: {
    title: "Calendar",
    subtitle: "Admin-only agenda · scheduled tickets + shifts · preview-first",
    today: "Today",
    loading: "Loading calendar…",
    filter_all_shops: "All shops",
    filter_all_operators: "All operators",
    totals_line: "{tickets} scheduled ticket(s) · {shifts} shift(s) · {unscheduled} unscheduled",
    weekdays: { mon: "Mo", tue: "Tu", wed: "We", thu: "Th", fri: "Fr", sat: "Sa", sun: "Su" },
    shift_chip: "{count} shift(s)",
    day_panel_title: "Day {date}",
    day_panel_empty: "No tickets or shifts on this day.",
    tickets_label: "Tickets",
    shifts_label: "Shifts",
    unscheduled_title: "Unscheduled tickets (active)",
    unscheduled_empty: "No active unscheduled tickets.",
    unscheduled_hint: "Scheduling reuses the existing tickets PATCH · no new write endpoints.",
    schedule_btn: "Schedule",
  },

  operators: {
    title: "Operators",
    page_header: "Workforce · Operators",
    counter: "{count} operator",
    counter_plural: "{count} operators",
    empty: "No operators yet.",
    invite_submit: "Invite operator",
    placeholder_user_id: "user_id (existing TEAM user)",
    placeholder_label: "display label",
    placeholder_org: "organisation_id",
    columns: {
      label: "Label",
      user: "User",
      status: "Status",
      user_link_status: "User link",
      tension_preset: "Tension preset",
      actions: "Actions",
    },
    status_active: "active",
    status_inactive: "inactive",
    user_link_status: {
      filter_label: "User link filter",
      filter_all: "All",
      linked: "linked",
      orphan_review: "orphan · review",
      orphan_safe: "orphan · safe",
    },
    toggle_btn: "Toggle status",
    toggle_title: "Toggle operator status?",
    toggle_description: "Switch {label} from {from} to {to}.",
    toggle_confirm_label: "Toggle",
    tension_preset: {
      title: "Personal tension preset",
      admin_only: "Admin · staff only",
      hint: "Optional. Acts as an OPERATIONAL SUGGESTION when no shop preset applies. The tension hierarchy is: 1) manual edit · 2) customer/profile/QR (future, BLOCKED) · 3) shop preset · 4) operator preset · 5) empty. Manual edits and shop presets always win.",
      empty: "No personal preset configured.",
      summary_empty: "—",
      unit: "Unit",
      main: "Main (vertical)",
      cross: "Cross (horizontal)",
      save: "Save preset",
      saving: "Saving…",
      clear: "Clear preset",
      cancel: "Cancel",
    },
    resnapshot: {
      title: "Re-snapshot linked-user search tokens",
      subtitle: "Admin · staff only. Refreshes operator email + name search snapshots against the current users collection. Dry-run first; apply is gated by a typed confirmation.",
      locked_notice: "Locked — admin / staff only.",
      shop_filter_label: "Shop (optional)",
      shop_filter_all: "All shops",
      dry_run_btn: "Run dry-run",
      running: "Running…",
      empty: "Run a dry-run to see how many operator snapshots need refreshing.",
      totals_reviewed: "reviewed",
      totals_would_update: "would update",
      totals_unchanged: "unchanged",
      totals_no_user_id: "no user_id",
      totals_user_not_found: "user not found",
      totals_errors: "errors",
      totals_applied: "applied",
      bucket_would_update: "Would update ({count})",
      bucket_user_not_found: "User not found ({count})",
      bucket_user_not_found_hint: "These operators have a user_id that no longer resolves to a user. Snapshots are preserved on purpose; deciding policy (keep / wipe / suspend) is a separate slice.",
      diff_before: "Before",
      diff_after: "After",
      diff_truncated: "Showing {shown} of {total} entries; refine the shop filter to narrow further.",
      apply_explain: "Apply will refresh {count} operator snapshot(s). It writes ONLY linked_user_email_snapshot and linked_user_display_name_snapshot, never user_id, display_label, tickets, inventory, shops or users.",
      apply_confirm_label: "Type {word} to confirm",
      apply_btn: "Apply re-snapshot",
      apply_result: "Apply finished. Operators refreshed: {count}.",
      no_pending: "Nothing to refresh — all snapshots already match.",
    },
  },

  shifts: {
    title: "Shifts",
    page_header: "Workforce · Shifts",
    counter: "{count} shift",
    counter_plural: "{count} shifts",
    empty: "No shifts yet.",
    subtitle: "Read-only-first surface. Shifts come from the pilot seed (POST /api/admin/racket-lab/_ops/pilot-seed) or the future create form (deferred to Sub-sprint B+).",
    columns: {
      shift: "Shift",
      shop: "Shop",
      starts: "Starts",
      status: "Status",
      actions: "Actions",
    },
    action_open: "Open",
    action_close: "Close",
    action_cancel: "Cancel",
    dialog_open_title: "Open shift?",
    dialog_close_title: "Close shift?",
    dialog_cancel_title: "Cancel shift?",
  },

  capabilities: {
    title: "Capabilities",
    page_header: "Workforce · Capabilities",
    subtitle: "Catalogue (cross-org) and Shop assignments",
    catalogue_section_title: "Catalogue (read-only)",
    shopcap_section_title: "Shop-capabilities",
    empty_catalogue: "No capability catalogue entries.",
    empty_shopcap: "No shop-capability assignments.",
    columns: {
      key: "Key",
      display_name: "Display name",
      status: "Status",
      shop: "Shop",
      capability: "Capability",
      actions: "Actions",
    },
    toggle_btn: "Toggle status",
    toggle_title: "Toggle shop-capability?",
    toggle_confirm_label: "Toggle",
  },

  qr_tokens: {
    list_title: "QR tokens",
    page_header: "QR · Tokens",
    counter: "{count} token",
    counter_plural: "{count} tokens",
    empty: "No QR tokens minted yet.",
    columns: { id: "ID", kind: "Kind", state: "State", entity: "Entity", scope: "Scope", expires: "Expires", organisation: "Organisation" },
    mint: {
      title: "Mint QR token (Track A pilot)",
      submit: "Mint",
      kind_label: "Kind",
      ttl_label: "TTL (s)",
      single_use_label: "Single-use",
      entity_label: "Entity",
      org_label: "Organisation",
      scope_label: "Scope",
      org_label_locked: "Organisation (locked to pilot)",
      entity_label_optional: "Entity ID (optional)",
      entity_placeholder: "ticket id, shop id, …",
      ttl_label_optional: "TTL seconds (optional, default per kind)",
      ttl_placeholder: "leave empty for default",
      footer_note: "Internal admin mint · no QR image rendered · raw token never logged · Track A only.",
    },
    revoke: {
      title: "Revoke QR token",
      submit: "Revoke",
      reason_label: "Reason",
      reason_placeholder: "Optional revocation reason",
      subtitle: "Soft-revoke only. Hard-delete is not exposed. Idempotent: second revoke is a no-op.",
      reason_label_required: "Reason (required, min 3 chars)",
      cancel_label: "Cancel",
    },
    detail: {
      title: "QR token detail",
      back: "Back to tokens",
      loading: "Loading token…",
      not_found: "Token {id} not found.",
      state_revoked: "revoked",
      state_active: "active",
      subtitle: "Raw token revealed only via click-to-reveal · scan_audit appended on resolve",
      back_label: "Back to QR tokens",
      error_prefix: "Error",
      error_unknown: "unknown",
      fields: {
        id: "ID",
        kind: "Kind",
        state: "State",
        expires_at: "Expires at",
        single_use: "Single use",
        used_at: "Used at",
        organisation: "Organisation",
        entity: "Entity",
        scope: "Scope",
        revoke_reason: "Revoke reason",
      },
      raw_section: {
        title: "Raw token value",
        subtitle: "Visible only on demand · NEVER logged · copy-once policy",
        reveal: "Reveal",
        hide: "Hide",
      },
      revoke_btn: "Revoke token",
      audit_timeline_title: "Audit timeline",
      scan_audit_timeline_title: "Scan audit timeline",
      raw_document_title: "Raw document",
    },
    resolve: {
      title: "Resolve viewer",
      token_required: "Token value is required",
    },
  },

  notifications: {
    envelopes: {
      list_title: "Envelopes",
      page_header: "Notifications · Envelopes",
      counter: "{count} envelope",
      counter_plural: "{count} envelopes",
      empty: "No envelopes yet.",
      create_title: "Create draft envelope",
      detail_title: "Envelope detail",
      back: "Back to envelopes",
      loading: "Loading envelope…",
      not_found: "Envelope {id} not found.",
      columns: { id: "ID", template: "Template", channel: "Channel", state: "State", to_role: "To role", recipient: "Recipient", created: "Created", ticket: "Ticket" },
      filter_template_placeholder: "e.g. racketLab.email.ready_for_pickup",
      filter_ticket_placeholder: "ticket id",
      origin_auto: "auto",
      detail: {
        subtitle: "Logical state machine · dispatch-mock is mock-only · no real provider call",
        error_prefix: "Error",
        error_unknown: "unknown",
        fields: {
          id: "ID",
          status: "Status",
          template: "Template",
          channel: "Channel",
          recipient: "Recipient",
          language: "Language",
          from_role: "From role",
          org: "Org",
          ticket: "Ticket",
          dry_run: "Dry run",
          dispatch_ref: "Dispatch ref",
          dispatched_at: "Dispatched at",
          last_error: "Last error",
        },
        actions: {
          queue_btn: "Transition → queued",
          queue_title: "Transition envelope to queued",
          queue_description: "No real provider call. State machine only.",
          queue_confirm: "Queue",
          cancel_btn: "Cancel",
          cancel_title: "Cancel envelope",
          cancel_description: "Terminal state. No further transitions.",
          cancel_confirm: "Cancel envelope",
          preview_btn: "Preview (dry-run render)",
          dispatch_btn: "Dispatch (mock)",
          dispatch_title: "Dispatch via mock adapter",
          dispatch_description: "MOCK-ONLY · zero email · no provider call · Q28 real OFF · dispatch real OFF · Captures envelope into notifications_mock_outbox.",
          dispatch_confirm: "Dispatch (mock)",
        },
        preview_title: "Preview (dry-run · not persisted)",
        audit_timeline_title: "Audit timeline",
        raw_document_title: "Raw document",
      },
      form: {
        template_label: "Template key",
        channel_label: "Channel (derived)",
        recipient_label: "Recipient user_id",
        recipient_placeholder: "uuid of recipient (internal pilot user)",
        language_label: "Language",
        from_role_label: "From role",
        footer_note: "Always starts as draft · dry_run HARD true · NO real send.",
      },
    },
    mock_outbox: {
      list_title: "Mock outbox",
      page_header: "Notifications · Mock outbox",
      counter: "{count} item",
      counter_plural: "{count} items",
      empty: "No mock outbox items yet.",
      item_title: "Mock outbox item",
      back: "Back to mock outbox",
      loading: "Loading mock outbox item…",
      not_found: "Item {id} not found.",
      columns: { id: "ID", template: "Template", channel: "Channel", to_role: "To role", result: "Result", envelope: "Envelope", captured_at: "Captured at", created: "Created" },
      subtitle: "Capture-only · no provider call · TTL governed by env (default 90d)",
      filter_template_placeholder: "racketLab.email.ready_for_pickup",
      item: {
        subtitle: "Captured-only · zero real provider · zero email",
        error_prefix: "Error",
        error_unknown: "unknown",
        fields: {
          id: "ID",
          provider: "Provider",
          result: "Result",
          envelope_id: "Envelope ID",
          template: "Template",
          channel: "Channel",
          captured_at: "Captured at",
          captured_by: "Captured by",
          recipient_hash: "Recipient hash",
          redacted_reason: "Redacted reason",
        },
        captured_payload_title: "Captured payload",
      },
    },
  },

  resolve: {
    page_header: "QR · Resolve viewer",
    subtitle: "Paste a QR token to inspect its current state. No write side-effects.",
    token_label: "Token",
    token_placeholder: "qr_xxxxxxxxxx",
    submit: "Resolve",
    loading: "Resolving…",
    error_required: "Token value is required",
    error_not_found: "Token not found or revoked.",
    result_title: "Resolved token",
    no_result: "Paste a token above to begin.",
  },

  ops: {
    toolkit: {
      title: "Ops toolkit",
      sections: {
        promote: "Promote membership",
        gating: "Membership gating",
        scan_audit: "Scan audit overview",
        seed_helper: "Seed memberships helper",
      },
      page_subtitle: "D-α scope · admin / staff only · dry-run → apply contract enforced · Track A pilot only",
      scope_badge: "TEAMSOFTWAREINNOVATIONS internal only",
      complete_title: "Ops Toolkit complete (D-α + D-β-α + D-β-β).",
      complete_body: "Every admin endpoint catalogued in the D analysis is reachable through the admin shell.",
      tiles: {
        promote: {
          title: "Promote membership",
          body: "Idempotent S6 promote · dry-run + HMAC confirmation_token · soft-revocable",
        },
        gating: {
          title: "Membership gating",
          body: "In-memory override (ON / OFF / clear) · env state visible · resets on restart",
        },
        redis: {
          title: "Rate-limit & dispatch health",
          body: "Redis backend health · NP-R1 dispatch status · rate-limit reset (T1)",
        },
        scan_audit: {
          title: "Scan audit overview",
          body: "Aggregates over scan_audit[] · pre-lookup denials · 24h / 7d / total windows",
        },
        legacy: {
          title: "Legacy backfill (S9.1)",
          body: "Overview · classify one (T2) · batch apply (T3) · run report viewer",
        },
        retention: {
          title: "Retention purges (T3)",
          body: "QR pre-lookup denials · NP-R1 mock outbox · irreversible · dry-run mandatory",
        },
        seed_helper: {
          title: "Seed memberships helper",
          body: "Read-only · derives promote intents from latest pilot seed run · per-row Load-into-Promote only",
        },
      },
    },
    common: {
      back_to_toolkit: "Back to Ops Toolkit",
      error_prefix: "Error",
      error_unknown: "unknown",
      in_flight: "in flight",
      in_flight_ellipsis: "in flight…",
      run_dry_run_first: "Run dry-run first",
      input_changed_rerun: "Input changed · re-run dry-run",
      kill_switch_blocked: "kill_switch_active=true",
      reason_min_3: "Reason must be ≥3 chars",
    },
    promote: {
      title: "Promote membership",
      apply: "Apply promotion",
      rollback_apply: "Apply rollback",
      title_full: "Promote membership (S6)",
      subtitle: "Idempotent · HMAC `confirmation_token` required · soft-revocable · Track A only",
      intent_title: "Intent",
      prefill_banner: "Pre-filled from {source} · Dry-run still required",
      dry_run_btn: "Dry-run",
      reset_btn: "Reset",
      applied_line: "Applied · action={action} · at {at}",
      stats: {
        memberships: "memberships",
        active: "active",
        revoked: "revoked",
        pending_onboarding: "pending_onboarding",
        audit_recent: "audit_recent",
      },
      form: {
        user_id_label: "User ID",
        user_id_placeholder: "uuid of the target user",
        role_label: "Role",
        role_hint: "Allowed runtime roles only. Reserved customer / guardian / sponsor roles are blocked server-side.",
        org_label_locked: "Organisation ID (locked to pilot)",
        shop_ids_label: "Shop IDs (one per line, optional)",
        footer_note: "Internal pilot only · Track A · TEAMSOFTWAREINNOVATIONS · idempotent · soft-revocable.",
      },
    },
    rollback: {
      section_title: "Rollback intent (soft-revoke)",
      dry_run_btn: "Dry-run rollback",
      rolled_back_line: "Rolled back · action={action} · at {at}",
      form: {
        user_id_label: "User ID (target)",
        user_id_placeholder: "uuid of the user to rollback",
        reason_label: "Reason",
        reason_placeholder: "Reason (min 3 chars · stored in audit)",
        reason_short: "Reason must be at least 3 characters.",
        footer_note: "Soft-revoke only · status → revoked · audit entry appended · reversible via promote.",
      },
    },
    verify: {
      title: "Verify membership",
      user_id_label: "User ID",
      user_id_placeholder: "uuid",
      role_label_optional: "Role (optional)",
      role_placeholder: "e.g. shop_manager",
      submit: "Verify",
      user_id_required: "user_id required",
    },
    audit_viewer: {
      user_id_label: "User ID",
      user_id_placeholder: "uuid",
      submit: "Lookup",
      user_id_required: "user_id required",
    },
    dry_run: {
      empty: 'No dry-run yet. Fill the form and press "Dry-run".',
      stale_pill: "stale · re-run dry-run",
      killswitch_banner: "kill_switch_active=true — `RL_PROMOTION_DRY_RUN` is set in env. Apply is disabled regardless of confirmation_token.",
      token_label: "confirmation_token (masked):",
    },
    apply_strip: {
      type_to_enable: "Type {phrase} to enable the apply button.",
    },
    gating: {
      force_on: {
        title: "Force gating ON",
        description: "Forces membership gating ON for the entire process. Resets on restart.",
        confirm: "Force ON",
      },
      force_off: {
        title: "Force gating OFF",
        description: "Disables membership gating for the entire process. Use with care. Resets on restart.",
        confirm: "Force OFF",
      },
      clear_override: {
        title: "Clear gating override",
        description: "Falls back to the env flag. No env mutation. No restart.",
        confirm: "Clear override",
      },
      page_title: "Membership gating override",
      subtitle: "Admin only · in-memory · never mutates env · resets on process restart",
      loading: "Loading gating status…",
      current_state: "Current state",
      current_state_hint: "env_flag · override · effective",
      override_active: "override active",
      value_unset: "(unset)",
      value_cleared: "(cleared)",
    },
    redis: {
      title: "Redis health",
      reset: "Reset",
      page_title: "Rate-limit & dispatch health",
      subtitle: "Read-only · Phase 2 R1/R2 backend (memory / fake_redis) · customer_facing_allowed must be false",
      card_title: "Rate-limit backend health",
      card_loading: "Loading…",
      breach_banner: "customer_facing_allowed=true — contract breach. STOP.",
      safe_banner: "customer_facing_allowed=false · internal only",
      rate_limit_reset_title: "Rate-limit reset",
      rate_limit_reset_subtitle: "Wipes the in-memory rate-limit buckets. Buckets rebuild on the next scan. Safe to call multiple times.",
      reset_buckets_btn: "Reset buckets",
      reset_dialog_title: "Reset rate-limit buckets",
      reset_dialog_description: "In-memory only · no DB write · safe to call multiple times · audit captured in supervisor logs.",
      reset_ok_at: "Reset OK · at {at}",
      full_snapshot: "Full snapshot",
    },
    scan_audit: {
      title: "Scan audit overview",
      subtitle: "Read-only · NO PII · NO raw token · NO raw IP · 24h / 7d / lifetime windows",
      loading: "Loading aggregates…",
      empty: "No data yet.",
      card_title: "Scan audit overview",
      generated_at_label: "generated_at:",
      filters: {
        org_label: "Organisation",
        kind_label: "Kind",
        result_label: "Result",
        limit_label: "Top-N limit",
        any_option: "(any)",
      },
      sections: {
        windows: "scan_audit windows",
        pre_lookup_windows: "pre-lookup denials windows",
        audit_flags_lifetime: "audit flags (lifetime)",
      },
      window_labels: {
        total: "total",
        last_7d: "7d",
        last_24h: "24h",
      },
      toplists: {
        by_result: "by_result",
        by_kind: "by_kind",
        top_scanners_opaque: "top_scanners (opaque)",
        top_organisation_ids: "top_organisation_ids",
        by_deny_reason_category: "by_deny_reason_category",
        pre_lookup_top_scanners: "pre-lookup top_scanners",
      },
    },
    legacy: {
      title: "Legacy memberships",
      tabs: {
        overview: "Overview",
        classify: "Classify one",
        batch: "Batch apply",
      },
      apply_classify: "Apply classify",
      apply_batch: "Apply batch",
      run_report: "Run report",
      page_title: "S9.1 Legacy memberships backfill",
      subtitle: "Privacy-safe overview · classify one (T2 HMAC) · batch apply (T3 HMAC, cap 200 rows, cross-org guarded)",
      overview: {
        loading: "Loading overview…",
        card_title: "Legacy memberships",
        generated_at_label: "generated_at:",
        columns: {
          user_hash: "user_hash",
          status: "status",
          joined_at: "joined_at",
          source: "source",
          rl_promotion: "rl_promotion",
          role: "role",
        },
      },
      classify: {
        user_id_label: "User ID",
        user_id_placeholder: "uuid",
        bucket_label: "Bucket",
        reason_label: "Reason (optional, stored in audit)",
        role_label_backfill: "Role (BACKFILL only)",
        org_label_locked: "Organisation (locked to pilot)",
        shop_ids_label: "Shop IDs (one per line, optional)",
        footer_note: "BACKFILL: idempotent upsert of rl_promotion · SOFT_REVOKE: status→revoked + audit · EXCLUDE: audit-only marker.",
        applied_line: "Applied · action={action} · bucket={bucket}",
      },
      batch: {
        json_label: "Batch JSON (array of {shape} · cap {maxRows} rows)",
        parse_error_array: "Root must be a JSON array",
        parse_error_too_many: "Too many rows ({n} > {max} cap)",
        parse_error_json: "JSON parse error: {msg}",
        row_error_not_object: "row {i}: not an object",
        row_error_user_id: "row {i}: missing/invalid user_id",
        row_error_bucket: "row {i}: bucket must be BACKFILL | SOFT_REVOKE | EXCLUDE",
        row_error_cross_org: "row {i}: cross-org BACKFILL not allowed (org={org})",
        row_error_count: "{count} row error(s)",
        row_more: "… and {count} more",
        preview_more: "… {count} more rows",
        dry_run_btn: "Dry-run batch",
        applied_line: "Applied · run_id={run_id} · applied={applied} · failed={failed}",
        view_run_report: "View run report →",
      },
      run_report_page: {
        back: "Back to Legacy",
        loading: "Loading…",
        page_title: "Legacy run report",
        card_title: "Run report",
        run_id_label: "run_id:",
        started_at_label: "started_at:",
        ended_at_label: "ended_at:",
        items_total_label: "items_total:",
        failures_header: "failures ({count})",
        columns: {
          user_hash: "user_hash",
          error_code: "error_code",
        },
      },
    },
    retention: {
      title: "Retention",
      warning_affects_only: "Affects only {collection} · delete_many semantics · NOT reversible",
      warning_real_purge: "Real purge deletes rows in {collection} via delete_many. No restore.",
      page_title: "Retention purges (T3)",
      page_subtitle_html: "Dry-run mandatory · real-run requires confirm + reason · {strong}",
      not_reversible: "NOT reversible",
      qr_title: "QR pre-lookup denials retention purge",
      mock_title: "NP-R1 mock-outbox retention purge",
      days_field_range_label: "{field} [{min},{max}]",
      dry_run_btn: "Dry-run",
      dry_run_result_title: "Dry-run · would delete",
      irreversible_title: "Irreversible operation",
      reason_label_min3: "Reason (min 3 chars)",
      apply_real_btn: "Apply real purge",
      purged_line: "Purged · deleted: {count}",
    },
    seed_helper: {
      title: "Seed memberships helper",
      no_applied_seed_run: "No applied seed run found for {org}.",
      subtitle: "Read-only · derives promote intents from the latest pilot seed run · per-row Load-into-Promote-form only · Apply still requires S6 Dry-run + typed-confirm APPLY.",
      reload_btn: "Reload",
      defensive_note_label: "Defensive note:",
      defensive_note_body: "Apply step still flows through S6 dry-run → typed-confirm APPLY. No bulk apply, no user creation, no membership mutation from this page.",
      filter_status_label: "Status",
      filter_role_label: "Role",
      seed_run_id_label: "seed_run_id:",
      seed_run_at_label: "seed_run_at:",
      loading: "loading…",
      no_seed_run_msg: "No applied seed run found for {org}. Run the pilot seed first via the admin seed endpoint.",
      no_match_filters: "No intents match the current filters.",
      columns: {
        user_id: "user_id",
        proposed_role: "proposed role",
        shop_ids: "shop_ids",
        status: "status",
        current: "current",
        action: "action",
      },
      row: {
        load_into_promote: "Load into Promote",
        copy_json: "JSON",
        synthetic_user_id_warning: "synthetic_user_id",
      },
    },
    audit: {
      promotion_lookup: "Audit promotion lookup",
      verify_membership: "Verify membership",
    },
  },

  roles: {
    admin: "Admin",
    staff: "Staff",
    shop_manager: "Shop manager",
    operator: "Operator",
    racket_technician: "Racket technician",
    head_coach: "Head coach",
    customer: "Customer",
    guardian: "Guardian",
    sponsor: "Sponsor",
    unknown: "Unknown role",
  },

  token_kinds: {
    worker_personal: "Worker personal",
    worker_self_open: "Worker self-open",
    customer_checkin: "Customer check-in",
    customer_handover: "Customer handover",
    equipment: "Equipment",
    shop: "Shop",
    ticket: "Ticket",
    event_checkin: "Event check-in",
    unknown: "Unknown kind",
  },

  ticket_status: {
    // RL-A.6 canonical (SS-1)
    draft: "Draft",
    checked_in: "Checked in",
    assigned: "Assigned",
    in_progress: "In progress",
    pending_customer_approval: "Pending customer approval",
    ready_for_pickup: "Ready for pickup",
    closed: "Closed",
    cancelled: "Cancelled",
    deleted: "Deleted",
    // Legacy R3 / scaffolding
    received: "Received",
    ready: "Ready",
    handed_over: "Handed over",
    on_hold: "On hold",
    requested: "Requested",
    accepted: "Accepted",
    pickup: "Pickup",
    diagnosis: "Diagnosis",
    pending_approval: "Pending approval",
    approved: "Approved",
    delivered: "Delivered",
    unknown: "Unknown status",
  },

  ticket_actions: {
    check_in: "Check-in",
    assign_operator: "Assign operator",
    assign_machine: "Assign machine",
    start: "Start work",
    request_approval: "Request approval",
    mark_approved: "Mark approved",
    finish: "Finish",
    pickup: "Pickup",
    cancel: "Cancel ticket",
    confirm_title: "{action}?",
    confirm_description: "Apply '{action}' to ticket {ticketId}. This will POST to the per-action endpoint.",
    terminal_state: "Terminal state — no further actions.",
    current_status_label: "Current status",
    audit_timeline_title: "Audit timeline",
  },

  envelope_status: {
    draft: "Draft",
    queued: "Queued",
    dispatched: "Dispatched",
    failed: "Failed",
    cancelled: "Cancelled",
    unknown: "Unknown status",
  },

  membership_buckets: {
    backfill: "Backfill",
    soft_revoke: "Soft revoke",
    exclude: "Exclude",
    unknown: "Unknown bucket",
  },

  scan_audit: {
    granted: "Granted",
    denied_role: "Denied — role",
    denied_rate_limit: "Denied — rate limit",
    denied_revoked: "Denied — revoked",
    denied_expired: "Denied — expired",
    denied_cross_org: "Denied — cross-org",
    denied_cross_shop: "Denied — cross-shop",
    denied_unknown: "Denied — unknown",
    metric_total: "Total scans",
    metric_granted: "Granted",
    metric_denied: "Denied",
    metric_window: "Window",
    metric_top_role: "Top role",
    metric_top_kind: "Top kind",
    metric_top_shop: "Top shop",
  },

  retention: {
    dry_run: "Dry run",
    apply: "Apply",
    not_reversible: "NOT reversible",
    real_purge: "Real purge",
    no_restore: "No restore",
    delete_many_semantics: "delete_many semantics",
    affects_only: "Affects only",
    cancel: "Cancel",
    purge_now: "Purge now",
    rows_purged: "Rows purged",
    rows_evaluated: "Rows evaluated",
    cutoff_iso: "Cutoff (ISO)",
    select_collection: "Select collection",
  },

  flag_states: {
    on: "On",
    off: "Off",
    forced_on: "Forced ON",
    forced_off: "Forced OFF",
    unknown: "Unknown",
  },

  dispatch: {
    provider_mock: "Provider mock",
    dispatch_false: "Dispatch disabled",
    dispatch_true: "Dispatch enabled",
    q28_false: "Q28 disabled",
    q28_true: "Q28 enabled",
    read_only: "Read-only",
    internal_only: "Internal only",
    flag_provider: "provider",
    flag_mock: "mock",
    flag_dispatch: "dispatch",
    flag_q28: "q28",
    flag_mock_enabled: "mock_enabled",
    flag_dispatch_enabled: "dispatch_enabled",
    flag_q28_enabled: "q28_enabled",
    outbox_label: "outbox",
    ttl_days_suffix: "ttl {days}d",
    error_prefix: "err {status}",
  },

  inventory: {
    page_header: "String & consumables inventory",
    subtitle: "Admin-only · per-shop stock · auditable movements · no hard-delete",
    new_btn: "New item",
    consumption_note: "Linked tickets deduct stock automatically on close (SS-6.1) · every consumption is auditable and reversible via a compensating movement.",
    readonly_note: "Read-only view: you can check stock and movement history, but stock management is handled by your shop manager.",
    empty: "No inventory items yet.",
    status_active: "active",
    status_inactive: "inactive",
    low_badge: "LOW",
    low_source_shop: "shop",
    low_source_shop_hint: "Threshold inherited from this shop's default fallback (RACKET-LAB-ROLE-3).",
    toggle_title: "Change item status?",
    toggle_confirm: "Confirm",
    filters: {
      shop: "Shop",
      type: "Type",
      status: "Status",
      all: "All",
      low_stock_only: "Low stock only",
    },
    form_catalog: {
      label: "Catalog product (optional)",
      placeholder: "— Select from central catalog —",
      hint: "Autofills brand/model and stores an additive reference. Manual entry still works.",
    },
    columns: {
      shop: "Shop",
      type: "Type",
      item: "Brand / model",
      stock: "Stock (available / initial)",
      status: "Status",
      actions: "Actions",
    },
    types: {
      string_set: "String set",
      string_reel: "String reel",
      consumable: "Consumable",
    },
    units: {
      set: "set(s)",
      meter: "m",
      unit: "unit(s)",
    },
    actions: {
      adjust: "Adjust",
      edit: "Edit",
      movements: "History",
      activate: "Activate",
      deactivate: "Deactivate",
    },
    form: {
      title_create: "New inventory item",
      title_edit: "Edit inventory item",
      shop: "Shop *",
      shop_placeholder: "Select a shop…",
      type: "Type *",
      brand: "Brand *",
      brand_placeholder: "e.g. Babolat",
      model: "Model / string *",
      model_placeholder: "e.g. RPM Blast",
      gauge: "Gauge (optional)",
      gauge_placeholder: "e.g. 1.25mm / 17",
      unit: "Unit",
      initial_quantity: "Initial quantity",
      consumption: "Consumption per ticket",
      consumption_hint: "Units deducted automatically when a linked ticket closes (SS-6.1).",
      low_stock: "Low-stock threshold (optional)",
      low_stock_hint: "Alert (UI-only) when available stock falls to/below this value. No notifications.",
      unit_cost: "Unit cost (EUR, optional)",
      unit_cost_hint: "Used by the monthly report to estimate cost per shop. No customer-facing use.",
      notes: "Internal notes",
      anti_pii_hint: "No emails or phones · backend rejects '@' and 7+ digits.",
      cancel: "Cancel",
      submit_create: "Create item",
      submit_edit: "Save changes",
      submitting: "Saving…",
    },
    adjust: {
      title: "Adjust stock",
      current: "Current available",
      delta: "Adjustment (±) *",
      delta_hint: "Negative deducts · positive adds. Reversible via a compensating adjustment — both stay in the audit trail.",
      reason: "Reason *",
      reason_placeholder: "e.g. manual count correction / compensation",
      insufficient: "Stock cannot go below zero.",
      cancel: "Cancel",
      submit: "Apply adjustment",
      submitting: "Applying…",
    },
    movements: {
      title: "Stock movements",
      empty: "No movements recorded.",
      close: "Close",
      type_initial: "Initial",
      type_manual_adjust: "Manual adjust",
      type_consumption: "Consumption",
      compensate: "Compensate",
      compensated: "Compensated",
      columns: {
        at: "Date",
        type: "Type",
        delta: "Δ",
        range: "Before → after",
        reason: "Reason",
      },
    },
    report: {
      toggle: "Monthly report",
      title: "Monthly consumption report",
      subtitle: "Read-only · built on auditable consumption movements · QA universe excluded from operational counters",
      loading: "Loading report…",
      all_shops: "All shops",
      units_consumed: "Units consumed",
      net_units: "Net (after compensations)",
      movements_count: "Consumption movements",
      compensated: "Compensated units",
      tickets_closed: "Tickets closed (operational)",
      with_consumption: "Closed with consumption",
      coverage: "Coverage",
      cost: "Estimated cost (EUR)",
      net_cost: "Net cost (EUR)",
      missing_cost_note: "{n} consumed item(s) without unit cost — EUR figures are partial.",
      qa_closed: "QA tickets closed",
      qa_note: "QA/test tickets never consume stock and are reported separately — they are NOT mixed with operational data.",
      top_items_title: "Most consumed items",
      by_shop_title: "By shop",
      movements_title: "Consumption movements (latest)",
      ticket: "Ticket",
      empty: "No consumption recorded for this period.",
      months: {
        1: "January", 2: "February", 3: "March", 4: "April",
        5: "May", 6: "June", 7: "July", 8: "August",
        9: "September", 10: "October", 11: "November", 12: "December",
      },
    },
  },

  banners: {
    track_a_pilot_preview: "Track A pilot · Preview-only",
    pilot_only: "Pilot-only surface",
    requires_admin: "Admin-only surface",
    pilot_mode_primary: "PILOT MODE — TEAMSOFTWAREINNOVATIONS internal only",
    pilot_mode_secondary: "Read-only surface · No customer-facing · No Q28 · No apply real",
    dispatch_real_enabled_stop: "DISPATCH REAL ENABLED · STOP",
    dispatch_mock_only: "Mock-only · Q28 OFF · No provider call",
  },
};

export default racketLabEn;
